import { createQueryKeys } from '@lukemorales/query-key-factory';
import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';
import { PresentCardApi } from 'api';
import type { PresentCard } from 'api/methods/PresentCardApi';

/**
 * [PresentCard] Query key 생성
 * --
 */
export const presentCardQueryKeys = createQueryKeys('presentCard', {
  getPresentCard: (
    idx: number,
    params?: { [key: string]: string | number }
  ) => [idx, params],
  getPresentCards: (params?: { [key: string]: string | number }) => [params],
});

/**
 * [PresentCard] 선물 전체 조회
 * --
 */
export const useGetPresentCards = (
  params?: { [key: string]: string | number },
  options?: UseQueryOptions<any, any>
) => {
  const data = useQuery({
    queryKey: presentCardQueryKeys.getPresentCards(params).queryKey,
    queryFn: () => PresentCardApi.getPresentCards(params),
    ...options,
  });
  return data;
};

/**
 * [PresentCard] 선물 상세 조회
 * --
 */
export const useGetPresentCard = (
  idx: number,
  params?: { [key: string]: string | number },
  options?: UseQueryOptions<any, any>
) => {
  const data = useQuery({
    queryKey: presentCardQueryKeys.getPresentCard(idx, params).queryKey,
    queryFn: () => PresentCardApi.getPresentCard(idx, params),
    ...options,
  });

  return data;
};

/**
 * [PresentCard] 선물 생성
 * --
 */
export const useCreatePresentCard = (body: PresentCard) => {
  const { mutateAsync: createPresentCard, ...result } = useMutation({
    mutationFn: (variables: PresentCard) =>
      PresentCardApi.createPresentCard(variables),
  });

  return { createPresentCard, ...result };
};

/**
 * [PresentCard] 선물 수정
 * --
 */
export const useUpdatePresentCard = (body: PresentCard) => {
  const { mutateAsync: updatePresentCard, ...result } = useMutation({
    mutationFn: (variables: PresentCard) =>
      PresentCardApi.updatePresentCard(variables),
  });

  return { updatePresentCard, ...result };
};

/**
 * [PresentCard] 선물 삭제
 * --
 */
export const useDeletePresentCard = (idx: number) => {
  const { mutateAsync: deletePresentCard, ...result } = useMutation({
    mutationFn: (variables: number) =>
      PresentCardApi.deletePresentCard(variables),
  });

  return { deletePresentCard, ...result };
};

/**
 * [PresentCard] 선물 생성/수정
 * --
 */
export const useUpsertPresentCard = () => {
  const { mutateAsync: upsertPresentCard, ...result } = useMutation({
    mutationFn: (variables: PresentCard[]) =>
      PresentCardApi.upsertPresentCard(variables),
  });

  return { upsertPresentCard, ...result };
};
